import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
    ShoppingCart,
    Zap,
    Minus,
    Plus,
    ChevronLeft,
    Loader2,
    AlertCircle,
    ShieldCheck,
    Truck,
    MapPin,
    Package,
    BadgeCheck,
    Phone,
    CreditCard,
    Flame,
    Star,
    CheckCircle2,
    Gift
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { ProductGallery } from '../components/product/ProductGallery';
import { ProductCard } from '../components/product/ProductCard';
import { ReviewSection } from '../components/product/ReviewSection';
import { useCartStore } from '../store/cartStore';
import { productsApi, settingsApi } from '../lib/api';
import { formatPrice, scrollToTop } from '../lib/utils';
import { trackViewContent, trackAddToCart } from '../lib/tracking';
import toast from 'react-hot-toast';

export function ProductPage() {
    const { id, categorySlug, productSlug } = useParams<{ id?: string; categorySlug?: string; productSlug?: string }>();
    const navigate = useNavigate();
    const [quantity, setQuantity] = useState(1);

    const addItem = useCartStore((state) => state.addItem);
    const openCart = useCartStore((state) => state.openCart);

    // Determine if we're using slug or ID
    const useSlug = !!categorySlug && !!productSlug;

    // Fetch product by slug or ID
    const { data: product, isLoading, isError } = useQuery({
        queryKey: useSlug ? ['product', 'slug', categorySlug, productSlug] : ['product', id],
        queryFn: () => useSlug
            ? productsApi.getBySlug(categorySlug!, productSlug!)
            : productsApi.getById(id!),
        enabled: useSlug ? (!!categorySlug && !!productSlug) : !!id,
        staleTime: 1000 * 60 * 5,
    });

    // Fetch related products (same category)
    const { data: relatedData } = useQuery({
        queryKey: ['related', product?.category, id],
        queryFn: () => productsApi.getAll({
            limit: 4,
            category: product?.category || undefined
        }),
        enabled: !!product,
        staleTime: 1000 * 60 * 10,
    });

    // Fetch store settings
    const { data: settings } = useQuery({
        queryKey: ['store-settings'],
        queryFn: settingsApi.get,
        staleTime: 1000 * 60 * 10,
    });

    const relatedProducts = relatedData?.data?.filter(p => p.id !== id).slice(0, 4) || [];

    // Settings with defaults
    const topBannerEnabled = settings?.topBannerEnabled ?? true;
    const topBannerText = settings?.topBannerText ?? '🔥 Offre Limitée - Livraison GRATUITE aujourd\'hui!';
    const codEnabled = settings?.codEnabled ?? true;
    const codLabel = settings?.codLabel ?? 'Paiement à la livraison';
    const deliveryDays = settings?.deliveryDays ?? '2-5 jours ouvrés';
    const freeShippingEnabled = settings?.freeShippingEnabled ?? true;

    // Scroll to top on mount or route change
    useEffect(() => {
        scrollToTop();
    }, [id, categorySlug, productSlug]);

    // Track ViewContent when product loads
    useEffect(() => {
        if (product) {
            trackViewContent({
                contentId: product.id,
                contentName: product.name,
                contentCategory: product.category || undefined,
                value: product.sellingPrice || product.price,
                currency: 'DZD',
            });
        }
    }, [product?.id]);

    const handleAddToCart = () => {
        if (!product) return;
        const price = product.sellingPrice || product.price;

        // Track AddToCart event
        trackAddToCart({
            contentId: product.id,
            contentName: product.name,
            value: price * quantity,
            quantity: quantity,
            currency: 'DZD',
        });

        for (let i = 0; i < quantity; i++) {
            addItem({
                productId: product.id,
                name: product.name,
                price: price,
                imageUrl: product.imageUrl || product.images?.[0]?.url || '',
            });
        }

        toast.success(`${quantity} article(s) ajouté(s)`, {
            icon: '🛒',
            style: { background: '#333', color: '#fff', borderRadius: '12px' }
        });
        openCart();
    };

    const handleBuyNow = () => {
        handleAddToCart();
        navigate('/checkout');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
            </div>
        );
    }

    if (isError || !product) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
                <AlertCircle className="w-12 h-12 text-gray-300 mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Produit introuvable</h2>
                <button onClick={() => navigate('/')} className="btn-primary mt-4">
                    Retour à l'accueil
                </button>
            </div>
        );
    }

    const price = product.sellingPrice || product.price;
    const hasDiscount = product.sellingPrice && product.sellingPrice < product.price;
    const discountPercent = hasDiscount
        ? Math.round(((product.price - product.sellingPrice!) / product.price) * 100)
        : 0;
    const savings = hasDiscount ? product.price - product.sellingPrice! : 0;

    // Generate consistent random review count based on product ID (7-997)
    const getReviewCount = (productId: string) => {
        let hash = 0;
        for (let i = 0; i < productId.length; i++) {
            hash = ((hash << 5) - hash) + productId.charCodeAt(i);
            hash = hash & hash;
        }
        return Math.abs(hash % 991) + 7; // Range: 7-997
    };
    const reviewCount = getReviewCount(product.id);

    return (
        <div className="min-h-screen pb-36 md:pb-12 bg-gray-50">
            {/* Top Urgency Banner - Controlled by Settings */}
            {topBannerEnabled && (
                <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white py-2 px-3 md:px-4">
                    <div className="container-custom flex items-center justify-center gap-2 text-xs md:text-sm font-medium">
                        <Flame className="w-3 h-3 md:w-4 md:h-4 animate-pulse flex-shrink-0" />
                        <span className="truncate text-center">{topBannerText}</span>
                        <Flame className="w-3 h-3 md:w-4 md:h-4 animate-pulse flex-shrink-0 hidden sm:block" />
                    </div>
                </div>
            )}

            {/* Back Button */}
            <div className="bg-white border-b border-gray-100">
                <div className="container-custom py-2 md:py-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-1.5 md:gap-2 text-sm text-gray-500 hover:text-black active:text-black transition-colors py-1 touch-manipulation"
                    >
                        <ChevronLeft className="w-5 h-5 md:w-4 md:h-4" />
                        <span>Retour</span>
                    </button>
                </div>
            </div>

            <div className="container-custom py-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Gallery (Left) */}
                    <div className="lg:col-span-7">
                        <div className="bg-white rounded-2xl p-4 shadow-sm">
                            <ProductGallery
                                images={product.images || []}
                                mainImage={product.imageUrl}
                                productName={product.name}
                            />
                        </div>
                    </div>

                    {/* Product Info (Right - Sticky) */}
                    <div className="lg:col-span-5 relative">
                        <div className="lg:sticky lg:top-6 space-y-4">
                            {/* Main Product Card */}
                            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-5">
                                {/* Category & Badges */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {product.category && (
                                        <span className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-bold uppercase tracking-wide rounded-full">
                                            {product.category}
                                        </span>
                                    )}
                                    <span className="px-3 py-1 bg-green-50 text-green-600 text-xs font-bold rounded-full flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" />
                                        En Stock
                                    </span>
                                    {hasDiscount && (
                                        <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                                            -{discountPercent}% PROMO
                                        </span>
                                    )}
                                </div>

                                {/* Product Title */}
                                <h1 className="text-xl md:text-2xl lg:text-3xl font-display font-bold text-gray-900 leading-tight">
                                    {product.name}
                                </h1>

                                {/* Rating (Social Proof) */}
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                                        ))}
                                    </div>
                                    <span className="text-sm text-gray-500">({reviewCount} avis vérifiés)</span>
                                </div>

                                {/* Price Section */}
                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 space-y-2">
                                    <div className="flex items-end gap-3 flex-wrap">
                                        <span className="text-4xl font-bold text-gray-900 font-display">
                                            {formatPrice(price)}
                                        </span>
                                        {hasDiscount && (
                                            <span className="text-xl text-gray-400 line-through">
                                                {formatPrice(product.price)}
                                            </span>
                                        )}
                                    </div>
                                    {hasDiscount && (
                                        <div className="flex items-center gap-2 text-green-600 font-semibold">
                                            <Gift className="w-4 h-4" />
                                            <span>Vous économisez {formatPrice(savings)}!</span>
                                        </div>
                                    )}
                                </div>

                                {/* Quantity Selector */}
                                <div className="flex items-center justify-between py-2">
                                    <label className="text-sm font-semibold text-gray-700">Quantité</label>
                                    <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                                        <button
                                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                            className="w-11 h-11 md:w-10 md:h-10 flex items-center justify-center bg-white rounded-lg shadow-sm active:bg-gray-100 transition-colors touch-manipulation"
                                        >
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <span className="w-12 text-center font-bold text-lg tabular-nums">{quantity}</span>
                                        <button
                                            onClick={() => setQuantity(quantity + 1)}
                                            className="w-11 h-11 md:w-10 md:h-10 flex items-center justify-center bg-white rounded-lg shadow-sm active:bg-gray-100 transition-colors touch-manipulation"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* CTA Buttons */}
                                <div className="space-y-3">
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleBuyNow}
                                        className="w-full py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-bold text-lg rounded-xl shadow-lg shadow-primary-500/30 flex items-center justify-center gap-3 transition-all"
                                    >
                                        <Zap className="w-6 h-6" />
                                        Commander Maintenant
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleAddToCart}
                                        className="w-full py-4 bg-gray-900 hover:bg-gray-800 text-white font-bold text-lg rounded-xl flex items-center justify-center gap-3 transition-all"
                                    >
                                        <ShoppingCart className="w-5 h-5" />
                                        Ajouter au Panier
                                    </motion.button>
                                </div>

                                {/* COD Badge - Controlled by Settings */}
                                {codEnabled && (
                                    <div className="flex items-center justify-center gap-2 py-3 bg-green-50 border-2 border-green-200 rounded-xl">
                                        <CreditCard className="w-5 h-5 text-green-600" />
                                        <span className="font-bold text-green-700">💵 {codLabel}</span>
                                    </div>
                                )}
                            </div>

                            {/* Trust Signals Card */}
                            <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                    <BadgeCheck className="w-5 h-5 text-primary-500" />
                                    Pourquoi nous choisir?
                                </h3>
                                <div className="grid grid-cols-1 gap-3">
                                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                            <Truck className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900">Livraison 58 Wilayas</p>
                                            <p className="text-xs text-gray-500">Livraison rapide partout en Algérie</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                                            <ShieldCheck className="w-5 h-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900">Satisfait ou Remboursé</p>
                                            <p className="text-xs text-gray-500">Garantie 100% satisfaction</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
                                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                                            <Package className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900">Emballage Sécurisé</p>
                                            <p className="text-xs text-gray-500">Produit protégé pendant le transport</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl">
                                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                                            <Phone className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900">Service Client 7j/7</p>
                                            <p className="text-xs text-gray-500">Assistance par téléphone & WhatsApp</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Delivery Info - Controlled by Settings */}
                            <div className="bg-white rounded-2xl p-5 shadow-sm">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
                                    <MapPin className="w-5 h-5 text-primary-500" />
                                    Informations de Livraison
                                </h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Délai de livraison</span>
                                        <span className="font-semibold text-gray-900">{deliveryDays}</span>
                                    </div>
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Frais de livraison</span>
                                        <span className="font-semibold text-green-600">
                                            {freeShippingEnabled ? 'GRATUIT' : `${settings?.deliveryFee || 600} DA`}
                                        </span>
                                    </div>
                                    {codEnabled && (
                                        <div className="flex justify-between py-2">
                                            <span className="text-gray-600">Mode de paiement</span>
                                            <span className="font-semibold text-gray-900">{codLabel}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Description Section */}
                {product.description && (
                    <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-xl font-bold mb-6 font-display flex items-center gap-2">
                            <Package className="w-6 h-6 text-primary-500" />
                            Description du Produit
                        </h3>
                        <div
                            className="prose prose-lg prose-gray max-w-none prose-headings:font-display prose-headings:font-bold prose-img:rounded-xl"
                            dangerouslySetInnerHTML={{ __html: product.description }}
                        />
                    </div>
                )}

                {/* Reviews Section */}
                <ReviewSection productId={product.id} />

                {/* Related Products */}
                {relatedProducts.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-2xl font-bold font-display mb-6 flex items-center gap-2">
                            <Star className="w-6 h-6 text-yellow-500" />
                            Vous aimerez aussi
                        </h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {relatedProducts.map((p, idx) => (
                                <ProductCard key={p.id} product={p} index={idx} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Sticky Bar - Enhanced */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 pb-6 md:hidden z-40 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.1)] safe-area-bottom">
                <div className="flex items-center gap-3">
                    {/* Price on Mobile */}
                    <div className="shrink-0 min-w-0">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Prix total</p>
                        <p className="font-bold text-lg text-gray-900 tabular-nums">{formatPrice(price * quantity)}</p>
                        {quantity > 1 && (
                            <p className="text-[10px] text-gray-400">x{quantity} articles</p>
                        )}
                    </div>
                    <div className="flex-1 flex gap-2">
                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={handleAddToCart}
                            className="w-12 h-12 flex-shrink-0 bg-gray-900 text-white font-bold rounded-xl flex items-center justify-center touch-manipulation"
                            aria-label="Ajouter au panier"
                        >
                            <ShoppingCart className="w-5 h-5" />
                        </motion.button>
                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={handleBuyNow}
                            className="flex-1 h-12 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-primary-500/30 touch-manipulation"
                        >
                            <Zap className="w-5 h-5" />
                            <span>Commander</span>
                        </motion.button>
                    </div>
                </div>
            </div>
        </div>
    );
}
